import React, { useCallback, useEffect, useRef } from 'react';
import pickBy from 'lodash/pickBy';
import omit from 'lodash/omit';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse, getQueryParamNames } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { matchPathname, pathByRouteName } from '../../../util/routes';
import { createSearchPageUrl } from '../../SearchPage/SearchPage.shared';
import { markSearchSource } from '../../../analytics/searchEvent';
import {
  Avatar,
  Button,
  IconClose,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
} from '../../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarMobileCategoriesMenu from './TopbarMobileMenu/TopbarMobileCategoriesMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import { ReactComponent as ProfileIcon } from '../../../assets/ProfileIcon.svg';
import { ReactComponent as InboxIcon } from '../../../assets/InboxIcon.svg';
import IconHeart from '../../../components/IconHeart/IconHeart';

import css from './Topbar.module.css';
import { showCreateListingLinkForUser } from '../../../util/userHelpers';
import { resolveLogoLink } from '../../../util/logoLinks';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    currentSearchParams,
    marketplaceStats,
  } = props;

  const itemsCount = marketplaceStats?.items;
  const roundedCount = itemsCount ? `${Math.floor(itemsCount / 50) * 50}+` : null;
  const searchPlaceholder = roundedCount
    ? intl.formatMessage({ id: 'TopbarSearchForm.placeholder' }, { count: roundedCount })
    : intl.formatMessage({ id: 'TopbarSearchForm.placeholderFallback' });

  const handleSubmit = values => {
    const { currentSearchParams = {}, history, location, config, routeConfiguration } = props;

    const baseParams = isMainSearchTypeKeywords(config)
      ? omit(currentSearchParams, ['keywords'])
      : omit(
          currentSearchParams,
          isOriginInUse(config) ? ['address', 'bounds', 'origin'] : ['address', 'bounds']
        );

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        const keywordsValue = values?.keywords;
        return keywordsValue ? { keywords: keywordsValue } : {};
      }
      // topbar search defaults to 'location' search
      const locationValue = values?.location;
      const selectedPlace = locationValue?.selectedPlace;
      if (!selectedPlace) {
        return {};
      }
      const { origin, bounds } = selectedPlace || {};
      const originMaybe = isOriginInUse(config) && origin ? { origin } : {};
      const addressValue = locationValue?.search || '';
      if (!bounds) {
        return { ...originMaybe, address: addressValue };
      }

      return {
        ...originMaybe,
        address: addressValue,
        bounds,
      };
    };
    const searchParams = {
      ...baseParams,
      ...topbarSearchParams(),
    };

    // Use pushSearchRoute to correctly split categories into path segments
    pushSearchRoute(searchParams);
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);

  // The topbar inbox link always points to the combined "All" view; users can
  // then switch to the buyer- or seller-specific tabs from within InboxPage.
  const topbarInboxTab = 'all';

  const {
    mobilemenu,
    mobilesearch,
    mobilequick,
    desktopbuy,
    keywords: keywordsParam,
    q: qParam,
    address,
    origin,
    bounds,
  } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const keywords = keywordsParam || qParam;

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const logoLink = resolveLogoLink(config?.topbar?.logoLink);
  const restrictedMenuTexts = ['buy gear', 'about', 'about us', 'post a new listing'];
  const quickMenuLinks = customLinks.filter(linkConfig => {
    const rawText = linkConfig?.text;
    if (typeof rawText !== 'string') {
      return true;
    }
    const text = rawText.trim().toLowerCase();
    return !restrictedMenuTexts.includes(text);
  });
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);
  const isSearchResultsPage = [
    'SearchPage',
    'SearchPageCat1',
    'SearchPageCat2',
    'SearchPageCat3',
  ].includes(resolvedCurrentPage);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const pushSearchRoute = useCallback(
    searchParams => {
      // Purposeful topbar action: the source marker beats the previous-path fallback (searchEvent.js).
      markSearchSource('navbar');
      history.push(createSearchPageUrl(routeConfiguration, searchParams));
    },
    [history, routeConfiguration]
  );

  // Category nav items render as real anchors (crawlable); this builds their hrefs.
  const buildSearchUrl = useCallback(params => createSearchPageUrl(routeConfiguration, params), [
    routeConfiguration,
  ]);

  const handleAiSearchSubmit = useCallback(
    query => {
      if (query && query.trim()) {
        markSearchSource('navbar');
        history.push(
          createSearchPageUrl(routeConfiguration, { q: query.trim(), sort: 'relevance' })
        );
      }
    },
    [history, routeConfiguration]
  );

  const handleCategorySelect = useCallback(
    categoryParams => {
      const categoryKeys =
        config?.categoryConfiguration?.categoryLevelKeys?.map(levelKey => `pub_${levelKey}`) || [];
      const listingFieldsConfig = config?.listing?.listingFields || [];
      const defaultFiltersConfig = config?.search?.defaultFilters || [];
      const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

      // Read fresh params from the current URL to avoid stale closure values
      // when the user rapidly switches categories.
      const latestLocation = history?.location || location;
      const latestSearch = parse(latestLocation.search);
      const latestPathParams = matchPathname(latestLocation.pathname, routeConfiguration) || [];
      const matched = latestPathParams[0] || {};
      const { cat1, cat2, cat3 } = matched.params || {};
      const freshParams = { ...latestSearch };
      if (cat1) freshParams.pub_categoryLevel1 = cat1;
      if (cat2) freshParams.pub_categoryLevel2 = cat2;
      if (cat3) freshParams.pub_categoryLevel3 = cat3;

      // Selecting a category leaves the semantic search, so its query (q) and the
      // relevance sort it sets no longer apply and must be dropped.
      const relevanceKey = config?.search?.sortConfig?.relevanceKey || 'relevance';
      const relevanceSortMaybe = freshParams.sort === relevanceKey ? ['sort'] : [];

      const cleanedParams = omit(freshParams, [
        ...categoryKeys,
        ...filterQueryParamNames,
        ...relevanceSortMaybe,
        'q',
        'page',
        'desktopbuy',
        'mobilemenu',
        'mobilesearch',
        'mobilequick',
      ]);
      const sanitizedParams = Object.keys(categoryParams || {}).reduce((acc, key) => {
        const value = categoryParams[key];
        if (value != null) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const nextParams = { ...cleanedParams, ...sanitizedParams };
      pushSearchRoute(nextParams);
    },
    [config, history, location, routeConfiguration, pushSearchRoute]
  );

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const shouldAutoOpenDesktopBuyMenu = desktopbuy === 'open' && isSearchResultsPage;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';
  const isMobileQuickMenuOpen = isMobileLayout && mobilequick === 'open';
  const mobileSearchFormRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const mobileCategoriesRef = useRef(null);

  const getEmptyMobileSearchValues = useCallback(() => {
    return isMainSearchTypeKeywords(config) ? { keywords: '' } : { location: null };
  }, [config]);

  const closeMobileSearchModal = useCallback(() => {
    const formApi = mobileSearchFormRef.current;
    const emptyValues = getEmptyMobileSearchValues();
    mobileSearchInputRef?.current?.blur?.();
    if (formApi) {
      formApi.restart(emptyValues);
    }
    const latestLocation = history?.location || location;
    redirectToURLWithoutModalState(history, latestLocation, 'mobilesearch');
  }, [getEmptyMobileSearchValues, history, location]);
  const closeMobileSearchLabel = intl.formatMessage({ id: 'Modal.close' });

  const closeMobileMenu = useCallback(() => {
    redirectToURLWithoutModalState(history, location, 'mobilemenu');
  }, [history, location]);

  const closeMobileQuickMenu = useCallback(() => {
    redirectToURLWithoutModalState(history, location, 'mobilequick');
  }, [history, location]);

  useEffect(() => {
    if (!isMobileSearchOpen) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      mobileSearchInputRef?.current?.focus?.();
    }, 50);

    return () => window.clearTimeout(timer);
  }, [isMobileSearchOpen]);

  const handleDesktopBuyAutoOpenConsumed = useCallback(() => {
    if (desktopbuy === 'open') {
      redirectToURLWithoutModalState(history, location, 'desktopbuy');
    }
  }, [desktopbuy, history, location]);

  const mobileMenu = (
    <TopbarMobileCategoriesMenu
      ref={mobileCategoriesRef}
      categories={config?.categoryConfiguration?.categories || []}
      categoryLevelKeys={config?.categoryConfiguration?.categoryLevelKeys || []}
      onCategorySelect={handleCategorySelect}
      buildSearchUrl={buildSearchUrl}
      onClose={closeMobileMenu}
    />
  );
  const mobileQuickMenuRef = useRef(null);
  const mobileQuickMenu = (
    <TopbarMobileMenu
      ref={mobileQuickMenuRef}
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={quickMenuLinks}
      onClose={closeMobileQuickMenu}
      showCreateListingsLink={false}
      showManageListingsLink={showCreateListingsLink}
      inboxTab={topbarInboxTab}
    />
  );
  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageCat1', 'SearchPageCat2', 'SearchPageCat3'].includes(
      resolvedCurrentPage
    );
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const mobileSearchButton =
    showSearchForm && !isMobileSearchOpen ? (
      <button
        type="button"
        className={css.mobileIconButton}
        onClick={() => redirectToURLWithModalState(history, location, 'mobilesearch')}
        title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
      >
        <SearchIcon
          className={css.mobileSearchIcon}
          ariaLabel={intl.formatMessage({ id: 'Topbar.searchIcon' })}
        />
      </button>
    ) : null;

  // Auth state is known during SSR (see server/dataLoader.js), so the mobile
  // bar renders the final controls on first paint; CSS hides it on desktop.
  const mobileRightControls = isAuthenticated ? (
    <div className={css.rightControls}>
      {mobileSearchButton}
      <NamedLink className={css.mobileIconLink} name="InboxPage" params={{ tab: topbarInboxTab }}>
        <InboxIcon className={css.mobileInboxIcon} />
        {notificationDot}
      </NamedLink>
      <button
        type="button"
        className={css.mobileIconButton}
        onClick={() => redirectToURLWithModalState(history, location, 'mobilequick')}
        title={intl.formatMessage({ id: 'Topbar.profileMenuIcon' })}
      >
        <Avatar className={css.mobileAvatar} user={currentUser} disableProfileLink />
      </button>
      <NamedLink className={css.mobileSellButton} name="NewListingPage">
        <FormattedMessage id="TopbarDesktop.sell" />
      </NamedLink>
    </div>
  ) : (
    <div className={css.rightControls}>
      {mobileSearchButton}
      <NamedLink className={css.mobileLoginButton} name="LoginPage">
        <FormattedMessage id="TopbarDesktop.loginButton" />
      </NamedLink>
      {/* Guests land on the /sell marketing page first; on it (or signed in) the button
          starts the listing flow directly. */}
      <NamedLink
        className={css.mobileSellButton}
        name={resolvedCurrentPage === 'SellPage' ? 'NewListingPage' : 'SellPage'}
      >
        <FormattedMessage id="TopbarDesktop.sell" />
      </NamedLink>
    </div>
  );

  return (
    <div className={classes}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <div className={css.mobileLeft}>
          <button
            type="button"
            className={css.menuButton}
            onClick={() => {
              const loc = history?.location || location;
              if (isMobileMenuOpen) {
                if (mobileCategoriesRef.current?.triggerClose) {
                  mobileCategoriesRef.current.triggerClose();
                } else {
                  redirectToURLWithoutModalState(history, loc, 'mobilemenu');
                }
              } else {
                redirectToURLWithModalState(history, loc, 'mobilemenu');
              }
            }}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            {isMobileMenuOpen ? (
              <IconClose rootClassName={css.closeMenuIcon} />
            ) : (
              <MenuIcon className={css.menuIcon} />
            )}
          </button>
          <LinkedLogo
            layout={'mobile'}
            alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
            linkToExternalSite={logoLink}
          />
        </div>
        {mobileRightControls}
      </div>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          logoLink={logoLink}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          onAiSearchSubmit={handleAiSearchSubmit}
          config={config}
          customLinks={customLinks}
          searchPlaceholder={searchPlaceholder}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          inboxTab={topbarInboxTab}
          onManageDisableScrolling={onManageDisableScrolling}
          onCategorySelect={handleCategorySelect}
          buildSearchUrl={buildSearchUrl}
          autoOpenBuyMenu={shouldAutoOpenDesktopBuyMenu}
          onAutoOpenBuyMenuHandled={handleDesktopBuyAutoOpenConsumed}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        className={css.categoryModalRoot}
        containerClassName={css.categoryModalContainer}
        scrollLayerClassName={css.categoryModalScrollLayer}
        isOpen={isMobileMenuOpen}
        onClose={() => {
          if (mobileCategoriesRef.current?.triggerClose) {
            mobileCategoriesRef.current.triggerClose();
          } else {
            redirectToURLWithoutModalState(history, location, 'mobilemenu');
          }
        }}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        hideCloseButton
        closeOnScrollLayerClick
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileQuickMenu"
        containerClassName={css.modalQuickContainer}
        scrollLayerClassName={css.modalQuickScrollLayer}
        isOpen={isMobileQuickMenuOpen}
        onClose={() => {
          if (mobileQuickMenuRef.current?.triggerClose) {
            mobileQuickMenuRef.current.triggerClose();
          } else {
            redirectToURLWithoutModalState(history, location, 'mobilequick');
          }
        }}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        hideCloseButton
        closeOnScrollLayerClick
      >
        {authInProgress ? null : mobileQuickMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        scrollLayerClassName={css.modalSearchScrollLayer}
        isOpen={isMobileSearchOpen}
        onClose={closeMobileSearchModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        hideCloseButton
        closeOnScrollLayerClick
      >
        <div className={css.searchContainer}>
          <div className={css.searchHeader}>
            <TopbarSearchForm
              className={css.mobileSearchForm}
              onSubmit={handleSubmit}
              onAiSearch={handleAiSearchSubmit}
              initialValues={initialSearchFormValues}
              isMobile
              appConfig={config}
              formApiRef={mobileSearchFormRef}
              inputRef={mobileSearchInputRef}
              autoFocus={isMobileSearchOpen}
              placeholder={searchPlaceholder}
            />
            <button
              type="button"
              className={css.searchCloseButton}
              onClick={closeMobileSearchModal}
              aria-label={closeMobileSearchLabel}
            >
              <IconClose rootClassName={css.searchCloseIcon} ariaLabel={closeMobileSearchLabel} />
            </button>
          </div>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
